import { WorkOrderModule } from "./features/production-work-orders/WorkOrderModule";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./app/App";
import { LabelsModule } from "./features/labels/LabelsModule";
import { readLabelPinEntry } from "./features/labels/pin-entry";
import {
  TabletTerminalPage,
  shouldOpenTabletTerminal,
} from "./features/production-execution/TabletTerminalPage";
import { AccountFinanceModule } from "./features/account-finance/AccountFinanceModule";
import { CashFlowModule } from "./features/account-finance/CashFlowModule";
import { AccountMovementTablePage } from "./features/account-finance/AccountMovementTablePage";
import SupplierOfferPortal from "./features/procurement/SupplierOfferPortal";
import OrderConfirmationPortal from "./features/purchase-orders/presentation/OrderConfirmationPortal";
import TechnicalDepartmentBoard from "./features/production-execution/TechnicalDepartmentBoard";
import { PlcBenchPage } from "./features/production-execution/PlcBenchPage";
import { Sarf39Page } from "./features/sarf39/Sarf39Page";
import { Week39PackagingPage } from "./features/manual-packaging/Week39PackagingPage";
import { GeneralProductionPage } from "./features/general-production/GeneralProductionPage";
import { configureSarf39Install } from "./features/sarf39/sarf39-offline";
import PublicProductionDailyReport from "./features/production-reports/PublicProductionDailyReport";
import { ProductionAnalysisModule } from "./features/production-manager/ProductionAnalysisModule";
import { ProductionLineMonitorModule } from "./features/production-manager/ProductionLineMonitorModule";
import { ShiftProductionFormModule } from "./features/shift-production-form/ShiftProductionFormModule";
import { ShiftFormApprovalModule } from "./features/shift-form-approval/ShiftFormApprovalWorkspace";
import { approvalStageForPath, canonicalApprovalLocation } from "../shared/shift-production-form/approval-routes";
import "./styles/foundation.css";
import "./styles/production-execution.css";
import "./styles/accessibility-overrides.css";
import "./styles/contracts.css";
import "./styles/completed-records.css";
import "./styles/application-shell.css";
import "./styles/procurement-requests.css";
import "./styles/feedback-surfaces.css";
import "./styles/production-requirements.css";
import "./styles/production-management.css";
import "./styles/quality-capa.css";
import "./styles/planning-procurement.css";
import "./styles/modal-surfaces.css";
import "./styles/procurement-decisions.css";
import "./styles/procurement-workflows.css";
import "./styles/manager-decision.css";
import "./styles/specialist-workspace.css";
import "./styles/shared-select.css";
import "./styles/procurement-queue.css";
import "./styles/focus-overrides.css";
import "./styles/rfq-evaluation.css";
import "./styles/production-console.css";
import "./styles/form-controls.css";
import "./styles/operator-terminal.css";
import "./styles/supplier-offer-portal.css";
import "./styles/order-confirmation-portal.css";
import "./features/general-production/general-production.css";
import {
  ErrorBoundary,
  installGlobalErrorReporting,
} from "./shared/errors/ErrorBoundary";
import { platformBrand } from "./shared/branding";

const root = document.getElementById("root");

if (!root) {
  throw new Error("Uygulama kök elemanı bulunamadı.");
}

const labelEntry = readLabelPinEntry(window.location, window.history);
installGlobalErrorReporting();
const canonicalApprovalUrl = canonicalApprovalLocation(
  window.location.pathname, window.location.search, window.location.hash,
);
if (canonicalApprovalUrl) {
  window.history.replaceState(window.history.state, "", canonicalApprovalUrl);
}

const workOrderRoute = window.location.pathname === "/is-emri-takibi";
const lineMonitorRoute = ["/hat-izleme", "/hat-izleme-demo"].includes(window.location.pathname);
if (window.location.pathname === "/hat-izleme-demo") {
  window.history.replaceState(window.history.state, "", `/hat-izleme${window.location.search}${window.location.hash}`);
}
const productionAnalysisTestRoute = window.location.pathname === "/uretim-analiz-test";
const productionAnalysisRoute = window.location.pathname === "/uretim-analiz" || productionAnalysisTestRoute;
const shiftProductionFormRoute =
  window.location.pathname === "/vardiya-uretim-formu";
const shiftApprovalStage = approvalStageForPath(window.location.pathname);
const shiftFormApprovalRoute =
  window.location.pathname === "/vardiya-form-onay" || shiftApprovalStage !== null;
const shiftFormId = new URLSearchParams(window.location.search).get("form");
const redirectApprovalFormToCurrentStage =
  shiftFormId !== null && shiftApprovalStage === null;
const tabletTerminal =
  !labelEntry.active && !workOrderRoute && !lineMonitorRoute && !productionAnalysisRoute && !shiftProductionFormRoute && !shiftFormApprovalRoute && shouldOpenTabletTerminal();
document.title = lineMonitorRoute
  ? "Tamtaş Silindir · Canlı hat izleme | ANL"
  : tabletTerminal
  ? `Operatör | ${platformBrand.name}`
  : `${platformBrand.name} | ${platformBrand.description}`;
if (window.location.pathname === "/technical-board") {
  document
    .querySelector<HTMLLinkElement>('link[rel="manifest"]')
    ?.setAttribute("href", "/manifest.webmanifest");
  document
    .querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]')
    ?.setAttribute("content", "ANL Teknik");
}
if (window.location.pathname === "/sarf39") configureSarf39Install();
if (window.location.pathname === "/elle-paketleme-39") {
  window.history.replaceState(
    window.history.state,
    "",
    `/elle-paketleme${window.location.search}${window.location.hash}`,
  );
}
if (window.location.pathname === "/elle-paketleme-canli") {
  window.history.replaceState(
    window.history.state,
    "",
    `/elle-paketleme${window.location.search}${window.location.hash}`,
  );
}

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js", { scope: "/" });
  });
}

createRoot(root).render(
  tabletTerminal ? (
    <StrictMode>
      <ErrorBoundary>
        <TabletTerminalPage />
      </ErrorBoundary>
    </StrictMode>
  ) : (
    <StrictMode>
      <ErrorBoundary>
        {labelEntry.active ? <LabelsModule /> : lineMonitorRoute ? <ProductionLineMonitorModule /> : workOrderRoute ? <WorkOrderModule /> : productionAnalysisRoute ? (
          <ProductionAnalysisModule testMode={productionAnalysisTestRoute} />
        ) : shiftFormApprovalRoute || (shiftProductionFormRoute && shiftFormId !== null) ? (
          <ShiftFormApprovalModule initialFormId={shiftFormId} routeStage={shiftApprovalStage} redirectToCurrentStage={redirectApprovalFormToCurrentStage} />
        ) : shiftProductionFormRoute ? (
          <ShiftProductionFormModule
            initialFormId={shiftFormId}
          />
        ) : window.location.pathname === "/finans" ? (
          <CashFlowModule />
        ) : window.location.pathname === "/cari-hareket-tablosu" ? (
          <AccountMovementTablePage />
        ) : ["/cari-finans", "/carifinans"].includes(
            window.location.pathname,
          ) ? (
          <AccountFinanceModule />
        ) : window.location.pathname === "/plc-tezgah" ? (
          <PlcBenchPage />
        ) : window.location.pathname.startsWith("/uretim-raporu") ? (
          <PublicProductionDailyReport />
        ) : window.location.pathname === "/supplier-access" ? (
          <SupplierOfferPortal />
        ) : window.location.pathname === "/order-access" ? (
          <OrderConfirmationPortal />
        ) : window.location.pathname === "/technical-board" ? (
          <TechnicalDepartmentBoard />
        ) : window.location.pathname === "/sarf39" ? (
          <Sarf39Page />
        ) : window.location.pathname === "/elle-paketleme" ? (
          <Week39PackagingPage />
        ) : window.location.pathname === "/genel-uretim" ? (
          <GeneralProductionPage />
        ) : (
          <App />
        )}
      </ErrorBoundary>
    </StrictMode>
  ),
);
